import React, { useState, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export interface Column<T> {
  header: React.ReactNode;
  accessor: keyof T | ((row: T) => React.ReactNode);
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  width?: string;
  className?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  searchable?: boolean;
  searchPlaceholder?: string;
  searchFields?: (keyof T)[];
  filterable?: boolean;
  filterOptions?: { label: string; value: string; field: keyof T }[];
  defaultFilter?: string;
  onFilterChange?: (filter: string) => void;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  renderActions?: (row: T) => React.ReactNode;
  className?: string;
  // Server-side pagination props
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  currentPage?: number;
  onSearchChange?: (search: string) => void;
}

function DataTable<T extends Record<string, any>>({
  data,
  columns,
  searchable = true,
  searchPlaceholder = 'Search...',
  searchFields,
  filterable = false,
  filterOptions,
  defaultFilter = 'all',
  onFilterChange,
  pageSizeOptions = [5, 10, 20, 50, 100],
  defaultPageSize = 10,
  emptyMessage = 'No data found',
  emptyIcon,
  loading = false,
  onRowClick,
  renderActions,
  className = '',
  totalCount,
  onPageChange,
  onPageSizeChange,
  currentPage: externalCurrentPage,
  onSearchChange,
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilter, setCurrentFilter] = useState(defaultFilter);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortConfig, setSortConfig] = useState<{ key: keyof T | null; direction: 'asc' | 'desc' }>({
    key: null,
    direction: 'asc',
  });

  // Filter data
  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply search
    if (searchable && searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((row) => {
        if (searchFields && searchFields.length > 0) {
          return searchFields.some((field) => {
            const value = row[field];
            return value?.toString().toLowerCase().includes(searchLower);
          });
        }
        // Search all string/number fields if no specific fields provided
        return Object.values(row).some((value) =>
          value?.toString().toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply filter
    if (filterable && filterOptions && currentFilter !== 'all') {
      const filterOption = filterOptions.find((opt) => opt.value === currentFilter);
      if (filterOption) {
        result = result.filter((row) => {
          const value = row[filterOption.field];
          if (filterOption.value === 'active') {
            return (value === 1 || value === true || value === '1' || value === 'active') && 
                   (!row.is_suspended || row.is_suspended === false || row.is_suspended === 0);
          }
          if (filterOption.value === 'inactive') {
            return value === 0 || value === false || value === '0' || value === 'inactive';
          }
          if (filterOption.value === 'suspended') {
            return row.is_suspended === true || row.is_suspended === 1 || row.is_suspended === '1';
          }
          return value?.toString() === filterOption.value;
        });
      }
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        const aStr = aValue.toString().toLowerCase();
        const bStr = bValue.toString().toLowerCase();
        
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, searchTerm, currentFilter, sortConfig, searchable, filterable, filterOptions, searchFields]);

  // Paginate data
  const paginatedData = useMemo(() => {
    // If totalCount is provided, we assume data is already paginated by server
    if (totalCount !== undefined) {
      return filteredData;
    }
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize, totalCount]);

  const totalPages = totalCount !== undefined 
    ? Math.ceil(totalCount / pageSize) 
    : Math.ceil(filteredData.length / pageSize);

  const handleSort = (key: keyof T) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Sync internal page state with external prop if provided
  React.useEffect(() => {
    if (externalCurrentPage !== undefined && externalCurrentPage !== currentPage) {
      setCurrentPage(externalCurrentPage);
    }
  }, [externalCurrentPage, currentPage]);

  const handleFilterChange = (value: string) => {
    setCurrentFilter(value);
    setCurrentPage(1);
    if (onFilterChange) {
      onFilterChange(value);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      if (onPageChange) {
        onPageChange(page);
      } else {
        setCurrentPage(page);
      }
    }
  };

  const renderCell = (column: Column<T>, row: T) => {
    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }
    return row[column.accessor]?.toString() || '-';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-soft p-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
        <p className="mt-4 text-secondary-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-soft overflow-hidden ${className}`}>
      {/* Search and Filter Bar */}
      <div className="p-4 border-b border-secondary-200 bg-secondary-50">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
          {/* Search & Filter Group */}
          <div className="flex flex-col sm:flex-row flex-1 gap-4 w-full">
            {/* Search */}
            {searchable && (
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={18} />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    setCurrentPage(1);
                    if (onSearchChange) {
                      onSearchChange(value);
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>
            )}

            {/* Filter */}
            {filterable && filterOptions && (
              <div className="w-full sm:w-auto">
                <select
                  value={currentFilter}
                  onChange={(e) => handleFilterChange(e.target.value)}
                  className="w-full sm:w-auto px-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-sm"
                >
                  {!filterOptions.some(opt => opt.value === 'all') && <option value="all">All</option>}
                  {filterOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Page Size & Info */}
          <div className="flex items-center justify-between sm:justify-end gap-3 w-full lg:w-auto pt-2 lg:pt-0 border-t lg:border-t-0 border-secondary-100 lg:border-transparent">
            <div className="flex items-center gap-2">
              <span className="text-sm text-secondary-600">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-2 py-1.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-sm"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <span className="text-xs sm:text-sm text-secondary-500 whitespace-nowrap">
              {totalCount !== undefined ? totalCount : filteredData.length} results
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      {paginatedData.length === 0 ? (
        <div className="text-center py-20">
          {emptyIcon}
          <p className="text-secondary-600 font-semibold mb-2 mt-4">{emptyMessage}</p>
          {searchTerm && (
            <p className="text-sm text-secondary-500">Try adjusting your search or filters</p>
          )}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary-50 border-b border-secondary-200">
                <tr>
                  {columns.map((column, index) => (
                    <th
                      key={index}
                      className={`px-6 py-4 text-sm font-semibold text-secondary-900 ${
                        column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'
                      } ${column.sortable ? 'cursor-pointer hover:bg-secondary-100' : ''} ${column.className || ''}`}
                      style={{ width: column.width }}
                      onClick={() => column.sortable && typeof column.accessor !== 'function' && handleSort(column.accessor as keyof T)}
                    >
                      <div className="flex items-center gap-2">
                        <span>{column.header}</span>
                        {column.sortable && sortConfig.key === column.accessor && (
                          <span className="text-primary-500">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                  {renderActions && (
                    <th className="px-6 py-4 text-sm font-semibold text-secondary-900 text-center">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200">
                {paginatedData.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={`hover:bg-secondary-50 transition-colors ${
                      onRowClick ? 'cursor-pointer' : ''
                    }`}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className={`px-6 py-4 ${
                          column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'
                        } ${column.className || ''}`}
                      >
                        {renderCell(column, row)}
                      </td>
                    ))}
                    {renderActions && (
                      <td className="px-6 py-4 text-center">
                        {renderActions(row)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-secondary-200 bg-secondary-50">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-secondary-600">
                  Showing {(currentPage - 1) * pageSize + 1} to{' '}
                  {Math.min(currentPage * pageSize, totalCount !== undefined ? totalCount : filteredData.length)} of {totalCount !== undefined ? totalCount : filteredData.length} entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-secondary-300 hover:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                  >
                    <ChevronsLeft size={18} />
                  </button>
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-secondary-300 hover:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => goToPage(pageNum)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === pageNum
                              ? 'bg-primary-500 text-white'
                              : 'border border-secondary-300 hover:bg-secondary-100 text-secondary-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-secondary-300 hover:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <button
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-secondary-300 hover:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                  >
                    <ChevronsRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DataTable;

